export class FrameMissingHandler {
  static get htmlContentTypeRegexp() {
    return /^(?:text\/([^\s;,]+\b)?html|application\/xhtml\+xml)\b/;
  }

  static onEvent(event) {
    const {
      detail: { response, visit },
    } = event;

    new FrameMissingHandler({ document, event, response, visit }).onEvent();
  }

  constructor({ document, event, response, visit }) {
    this.document = document;
    this.event = event;
    this.response = response;
    this.visit = visit;
  }

  get responseheaders() {
    return this.response.headers;
  }

  get responseStatus() {
    return this.response.status;
  }

  get responseUrl() {
    return this.response.url;
  }

  get responseLocation() {
    return this.responseheaders.get('Location');
  }

  get responseContentType() {
    return this.responseheaders.get('Content-Type');
  }

  get responseContentTypeIsHtml() {
    return (
      this.responseContentType &&
      this.responseContentType.match(this.constructor.htmlContentTypeRegexp)
    );
  }

  get responseOk() {
    return this.response.ok;
  }

  get responseRedirected() {
    return this.response.redirected || this.responseLocation;
  }

  onEvent() {
    this.event.preventDefault();

    if (this.responseRedirected) {
      this._onRedirected();
    } else if (this.responseOk) {
      this._onOk();
    } else {
      this._onError();
    }
  }

  // private

  _onOk() {
    window.location.href = this.responseUrl;
  }

  _onRedirected() {
    window.location.href = this.responseLocation || this.responseUrl;
  }

  _onError() {
    this._renderResponseHtml();
  }

  // This is all copied from Turbo's source and modified as needed. This is what
  // Turbo Drive's error renderer does.
  // See: https://github.com/hotwired/turbo/blob/main/src/core/drive/error_renderer.js
  async _renderResponseHtml() {
    if (this.responseContentTypeIsHtml) {
      const responseHtml = await this.response.clone().text();
      const domParser = new DOMParser().parseFromString(responseHtml, 'text/html');
      const { documentElement, head, body } = this.document;

      documentElement.replaceChild(domParser.head, head);
      documentElement.replaceChild(domParser.body, body);
      this._activateScriptElements(documentElement);
    }
  }

  _activateScriptElements(documentElement) {
    const scriptElements = documentElement.querySelectorAll('script');

    scriptElements.forEach((originalScriptElement) => {
      const { parentNode } = originalScriptElement;

      if (parentNode) {
        const element = this._activateScriptElement(originalScriptElement);
        parentNode.replaceChild(element, originalScriptElement);
      }
    });
  }

  _activateScriptElement(element) {
    if (element.getAttribute('data-turbo-eval') === 'false') {
      return element;
    }

    const createdScriptElement = this.document.createElement('script');
    const cspNonce = this._getCspNonce();

    if (cspNonce) {
      createdScriptElement.nonce = cspNonce;
    }
    createdScriptElement.textContent = element.textContent;
    createdScriptElement.async = false;
    this._copyElementAttributes(createdScriptElement, element);

    return createdScriptElement;
  }

  _getCspNonce() {
    const element = this._getMetaElement('csp-nonce');

    if (element) {
      const { nonce, content } = element;
      return nonce === '' ? content : nonce;
    }

    return null;
  }

  _getMetaElement(name) {
    return this.document.querySelector(`meta[name="${name}"]`);
  }

  _copyElementAttributes(destinationElement, sourceElement) {
    Array.from(sourceElement.attributes).forEach((attribute) => {
      destinationElement.setAttribute(attribute.name, attribute.value);
    });
  }
}
